// 非編集表示（MarkdownTextView）とコメント本文（CommentMessageBody）が共有する、
// Markdown の 1 行（`div[data-md-line]`）の描画。
//
// - 行スタイル・CSS クラス・表示テキストの決定は markdown-text.ts（単一の真実）へ委ねる。
// - **リンクの描画だけは呼び出し元ごとに変える。** キャンバスオブジェクトは StyledLinkifiedText
//   （遅延オープン・ダブルクリック編集への譲歩）、コメント本文は LinkifiedCommentText
//   （`<a>` の標準挙動・stopPropagation・参照専用時の target 切り替え）を使っており、
//   共用しているのは装飾つき区間の描画（renderInlineDecoratedSpans）だけである。
//   統一するとコメント内リンクの挙動が変わるため、レンダラーを引数で受け取る。
// - callout（MarkdownCalloutView）の本文行も同じコンポーネントを通す。callout は行を囲むだけで、
//   行の作り方は callout の外とまったく同じにする。

import {
  buildTextInlineDisplaySpans,
  type MarkdownSourceMap,
  type TextInlineDisplaySpan,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import type { CSSProperties, KeyboardEvent, MouseEvent } from "react";
import {
  MARKDOWN_TASK_BOX_CLASS,
  markdownLineClassName,
  markdownLineStyleFor,
  markdownListMarkerClassName,
  markdownListMarkerStyleFor,
  markdownListMarkerTextFor,
  markdownTextLineFromParts,
  type MarkdownTextLine,
} from "../../../lib/markdown-text";

/** 行の表示区間（部分書式 + 記法の装飾 + リンク）を描くレンダラーへ渡す値。 */
export interface MarkdownInlineRenderProps {
  readonly spans: readonly TextInlineDisplaySpan[];
  /** 箇条書きのグリフや番号付きリストの項番など、run を持たない表示専用の接頭辞。 */
  readonly prefix?: string | undefined;
  /** 接頭辞の span に付けるクラス（マーカー領域・番号付きリストの項番）。 */
  readonly prefixClassName?: string | undefined;
  /**
   * 接頭辞の span へ与えるインラインスタイル（マーカー領域の幅）。
   * カスタムプロパティ（`--marker-width`）は React の型に無いため、ここまでで `CSSProperties` へ
   * 変換済みの値を渡す（変換はマーカーを組み立てる MarkdownLineView の 1 か所だけで行う）。
   */
  readonly prefixStyle?: CSSProperties | undefined;
  readonly headingLine?: boolean | undefined;
}

/** 呼び出し元ごとのリンク描画を差し込むためのレンダラー。 */
export type MarkdownInlineRenderer = (props: MarkdownInlineRenderProps) => JSX.Element;

interface Props {
  readonly sourceMap: MarkdownSourceMap;
  readonly lineIndex: number;
  /** 文字範囲だけへ適用する部分書式（正規形の run 配列）。 */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /** 直前の行の種別（連続するリスト行の項目間隔の判定に使う）。 */
  readonly previousKind?: MarkdownTextLine["kind"] | undefined;
  readonly renderInline: MarkdownInlineRenderer;
  /** 指定時だけ task 行を操作可能にする。引数は `[ ]` / `[x]` 内の文字の raw offset。 */
  readonly onToggleTask?: ((checkMarkOffset: number) => void) | undefined;
}

export function MarkdownLineView({
  sourceMap,
  lineIndex,
  inlineStyles,
  previousKind,
  renderInline,
  onToggleTask,
}: Props): JSX.Element {
  const sourceLine = sourceMap.lines[lineIndex]!;
  // 番号付きリストの項番は他行に依存するため、source map の採番結果を渡す。
  const line = markdownTextLineFromParts(sourceLine, {
    orderedLabel: sourceMap.orderedNumbers.get(sourceLine.lineIndex),
  });
  const style = markdownLineStyleFor(line, { previousKind });
  const className = markdownLineClassName(line);
  // 箇条書きのグリフ（`•`）と番号付きリストの項番（`1.`）は run を持たない表示専用の接頭辞。
  // マーカーと本文の間隔はマーカー領域の幅が作るため、末尾の区切り空白は span へ入れない。
  const prefix = markdownListMarkerTextFor(line);
  const spans = buildTextInlineDisplaySpans({
    line: sourceLine,
    runs: inlineStyles,
    text: sourceMap.text,
  });
  // 表示に現れる文字数で空行を判定する。装飾マーカーだけの行は、マーカーを隠した結果
  // 表示が 0 文字になるため、空行と同じく 1 行分の高さを維持する。
  // 水平線は本文（`---`）を表示しない契約（表示テキストは空）のため、常に空行と同じ構造にする。
  const hasVisibleText =
    line.kind !== "rule" && (prefix.length > 0 || spans.some((span) => span.text.length > 0));
  const interactiveTask =
    line.kind === "task" && sourceLine.checkMarkOffset !== undefined && onToggleTask !== undefined;
  const activateTask = (
    event: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>
  ): void => {
    if (!interactiveTask || sourceLine.checkMarkOffset === undefined || onToggleTask === undefined)
      return;
    event.preventDefault();
    event.stopPropagation();
    onToggleTask(sourceLine.checkMarkOffset);
  };
  const handleTaskClick = (event: MouseEvent<HTMLSpanElement>): void => {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    activateTask(event);
  };
  const handleTaskKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (event.key !== "Enter" && event.key !== " ") return;
    activateTask(event);
  };
  return (
    <div className={className} style={style} data-md-line={lineIndex}>
      {line.kind === "task" ? (
        <span
          aria-checked={interactiveTask ? line.checked : undefined}
          aria-label={
            interactiveTask
              ? line.content.length > 0
                ? line.content.slice(0, 100)
                : "空の項目"
              : line.checked
                ? "チェックボックス（完了）"
                : "チェックボックス（未完了）"
          }
          className={MARKDOWN_TASK_BOX_CLASS}
          data-canvas-task-checkbox="true"
          data-checked={String(line.checked)}
          onClick={interactiveTask ? handleTaskClick : undefined}
          onKeyDown={interactiveTask ? handleTaskKeyDown : undefined}
          role={interactiveTask ? "checkbox" : "img"}
          tabIndex={interactiveTask ? 0 : undefined}
        />
      ) : null}
      {/* 空行は <br> で 1 行分の高さを維持する（空 div は高さ 0 になるため）。 */}
      {hasVisibleText ? (
        renderInline({
          headingLine: sourceLine.kind === "heading",
          prefix,
          prefixClassName: markdownListMarkerClassName(line),
          prefixStyle: markdownListMarkerStyleFor(line) as CSSProperties | undefined,
          spans,
        })
      ) : line.kind !== "task" ? (
        <br />
      ) : null}
    </div>
  );
}
